/**
 * TASK-231: KYC Form Mapper
 *
 * Maps between ClientComplianceData and KYCFormData.
 */

import type { KYCFormData, PersonalInformation, ContactInformation, EmploymentInformation, TaxInformation, PEPStatus, Address, WealthSource, IncomeSource, BusinessActivities, PersonalBackground, AdverseMediaScreening } from '../../../types/kyc.types';
import { RiskLevel } from '../../../types/kyc.constants';
import type { ClientComplianceData, DataSource, TrackedField } from '../../../types/clientCompliance';
import { unwrap, createTrackedField, createDataSource } from '../../../types/clientCompliance';
import { transformDate } from '../transformers/dateTransformer';
import type { MapperOptions } from '../types';

/**
 * Join first and last name into full name
 */
export function joinNames(firstName?: string, lastName?: string): string {
  const parts = [firstName, lastName].filter(Boolean);
  return parts.join(' ');
}

/**
 * Extract numeric value from various formats
 * Handles: plain numbers, {amount, currency} objects, string numbers
 */
function extractNumericValue(value: unknown): number | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.-]/g, ''));
    return isNaN(parsed) ? undefined : parsed;
  }
  if (typeof value === 'object' && value !== null) {
    // Handle {amount, currency} format from extraction
    const obj = value as Record<string, unknown>;
    if ('amount' in obj && typeof obj.amount === 'number') {
      return obj.amount;
    }
    if ('value' in obj && typeof obj.value === 'number') {
      return obj.value;
    }
  }
  return undefined;
}

/**
 * Map ClientComplianceData to KYCFormData
 *
 * @param data - Shared compliance data
 * @param options - Mapper options
 * @returns Partial KYC form data
 */
export function mapToKYC(
  data: ClientComplianceData,
  _options?: MapperOptions
): Partial<KYCFormData> {
  const personal = data.personal || {};
  const contact = data.contact || {};
  const employment = data.employment || {};
  const tax = data.tax || {};
  const pep = data.pep || {};
  // TASK-262: New sections
  const wealthSummary = data.wealthSummary;
  const sharedWealthSources = data.wealthSources || [];
  const sharedIncomeSources = data.incomeSources || [];
  const sharedBusinessActivities = data.businessActivities || [];
  // TASK-320: Additional sections for prefill
  const personalBackground = data.personalBackground;
  const assetBreakdown = data.assetBreakdown;
  const additionalWealthInfo = data.additionalWealthInfo;
  const incomeExpectations = data.incomeExpectations;
  // TASK-321: Adverse media screening data
  const adverseMedia = data.adverseMedia;
  // TASK-322: Income summary data
  const incomeSummary = data.incomeSummary;

  // Build PersonalInformation
  const personalInfo: Partial<PersonalInformation> = {
    firstName: unwrap(personal.firstName) || '',
    middleName: unwrap(personal.middleName),
    lastName: unwrap(personal.lastName) || '',
    dateOfBirth: unwrap(personal.dateOfBirth) || '',
    placeOfBirth: unwrap(personal.placeOfBirth),
    gender: unwrap(personal.gender),
    maritalStatus: unwrap(personal.maritalStatus),
    nationalities: unwrap(personal.nationalities) || [],
    languages: unwrap(personal.languages),
    passportNumber: unwrap(personal.passportNumber),
    nationalId: unwrap(personal.nationalId),
    // TASK-321: SSN field
    ssn: unwrap(personal.ssn),
  };

  // Build ContactInformation
  const residentialAddress = unwrap(contact.residentialAddress);
  const contactInfo: Partial<ContactInformation> = {
    phonePrimary: unwrap(contact.phonePrimary) || '',
    phoneSecondary: unwrap(contact.phoneSecondary),
    emailPrimary: unwrap(contact.emailPrimary) || '',
    emailSecondary: unwrap(contact.emailSecondary),
    residentialAddress: residentialAddress || {
      street: '',
      city: '',
      postalCode: '',
      country: '',
    },
    mailingAddress: unwrap(contact.mailingAddress),
    preferredContactMethod: unwrap(contact.preferredContactMethod),
    // TASK-320: Relationship fields
    relationshipReason: unwrap(contact.relationshipReason),
    relationshipPurpose: unwrap(contact.relationshipPurpose),
  };

  // Build EmploymentInformation
  const employmentInfo: Partial<EmploymentInformation> = {
    employmentStatus: unwrap(employment.employmentStatus) || 'employed',
    employerName: unwrap(employment.employerName),
    occupation: unwrap(employment.occupation),
    employmentIndustry: unwrap(employment.employmentIndustry),
    yearsEmployed: unwrap(employment.yearsEmployed),
    workAddress: unwrap(employment.workAddress),
  };

  // Build TaxInformation
  const taxInfo: Partial<TaxInformation> = {
    taxResidence: unwrap(tax.taxResidence) || '',
    taxId: unwrap(tax.taxId),
    usPerson: unwrap(tax.usPerson) || false,
    fatcaStatus: unwrap(tax.fatcaStatus),
    taxJurisdictions: unwrap(tax.taxJurisdictions),
    // TASK-321: Extended tax fields
    usSSN: unwrap(tax.usSSN),
    taxExempt: unwrap(tax.taxExempt),
    taxExemptReason: unwrap(tax.taxExemptReason),
    additionalTaxResidencies: unwrap(tax.additionalTaxResidencies),
  };

  // Build PEPStatus
  const pepStatus: Partial<PEPStatus> = {
    isPEP: unwrap(pep.isPep) || false,
    isRCA: unwrap(pep.isRca) || false,
    pepType: unwrap(pep.pepType),
    countries: unwrap(pep.pepCountry) ? [unwrap(pep.pepCountry)!] : undefined,
    startDate: unwrap(pep.pepStartDate),
    endDate: unwrap(pep.pepEndDate),
    details: unwrap(pep.pepPosition),
    // TASK-321: RCA relationship fields
    rcaRelationship: unwrap(pep.rcaRelationship),
    rcaPEPName: unwrap(pep.rcaPepName),
  };

  // Calculate average confidence for metadata
  const confidenceScores = collectConfidenceScores(data);
  const avgConfidence = confidenceScores.length > 0
    ? confidenceScores.reduce((a, b) => a + b, 0) / confidenceScores.length
    : 0;

  // TASK-262: Map wealth sources from shared data
  // TASK-320 Phase 5: Extended wealth source fields
  const mappedWealthSources: WealthSource[] = sharedWealthSources.map((source) => ({
    sourceType: (unwrap(source.sourceType) || 'other') as WealthSource['sourceType'],
    sourceDescription: unwrap(source.sourceDescription) || '',
    percentage: unwrap(source.percentage) || 0,
    documented: unwrap(source.documented),
    // Timing fields
    acquisitionPeriod: unwrap(source.acquisitionPeriod),
    acquisitionStartDate: unwrap(source.acquisitionStartDate),
    acquisitionEndDate: unwrap(source.acquisitionEndDate),
    isOngoing: unwrap(source.isOngoing),
    // Asset details
    assetType: unwrap(source.assetType) as WealthSource['assetType'],
    assetValue: unwrap(source.assetValue),
    assetCurrency: unwrap(source.assetCurrency),
    assetLocation: unwrap(source.assetLocation),
    // Verification
    verificationNotes: unwrap(source.verificationNotes),
    supportingDocumentTypes: unwrap(source.supportingDocumentTypes) as WealthSource['supportingDocumentTypes'],
    // Liquidity & Risk
    liquidityLevel: unwrap(source.liquidityLevel) as WealthSource['liquidityLevel'],
    sourceRiskLevel: unwrap(source.riskLevel) as WealthSource['sourceRiskLevel'],
  }));

  // TASK-262: Map income sources from shared data
  // TASK-320 Phase 5: Extended income source fields
  const mappedIncomeSources: IncomeSource[] = sharedIncomeSources.map((source) => ({
    incomeType: (unwrap(source.incomeType) || 'other') as IncomeSource['incomeType'],
    incomeDescription: unwrap(source.incomeDescription),
    annualAmount: unwrap(source.annualAmount) || 0,
    currency: unwrap(source.currency) || 'CHF',
    frequency: unwrap(source.frequency) as IncomeSource['frequency'],
    // Source details
    sourceName: unwrap(source.sourceName),
    sourceCountry: unwrap(source.sourceCountry),
    startDate: unwrap(source.startDate),
    isOngoing: unwrap(source.isOngoing) ?? true,
    // Verification
    verified: unwrap(source.verified),
    supportingDocuments: unwrap(source.supportingDocuments),
    // Tax status
    taxPaid: unwrap(source.taxPaid),
    taxJurisdiction: unwrap(source.taxJurisdiction),
    taxRate: unwrap(source.taxRate),
    // Stability
    stabilityRating: unwrap(source.stabilityRating) as IncomeSource['stabilityRating'],
    expectedContinuity: unwrap(source.expectedContinuity) as IncomeSource['expectedContinuity'],
  }));

  // TASK-320 Phase 6: Map business activities from shared data
  const mappedBusinessActivities: BusinessActivities[] = sharedBusinessActivities.map((activity) => ({
    businessName: unwrap(activity.businessName) || '',
    businessType: (unwrap(activity.businessType) || 'other') as BusinessActivities['businessType'],
    registrationNumber: unwrap(activity.registrationNumber),
    registrationCountry: unwrap(activity.registrationCountry),
    primaryIndustry: unwrap(activity.primaryIndustry) || '',
    ownershipPercentage: unwrap(activity.ownershipPercentage),
    ownershipType: unwrap(activity.ownershipType) as BusinessActivities['ownershipType'],
    yearsInBusiness: unwrap(activity.yearsInBusiness),
    annualRevenue: unwrap(activity.annualRevenue),
    cashIntensive: unwrap(activity.cashIntensive),
    // TASK-321: Extended business activity fields
    industryRiskLevel: unwrap(activity.industryRiskLevel) as BusinessActivities['industryRiskLevel'],
    numberOfEmployees: unwrap(activity.numberOfEmployees),
    revenueCurrency: unwrap(activity.revenueCurrency),
    crossBorderTransactions: unwrap(activity.crossBorderTransactions),
    highRiskCountries: unwrap(activity.highRiskCountries),
  }));

  return {
    clientId: data.clientId,
    personalInfo: personalInfo as PersonalInformation,
    contactInfo: contactInfo as ContactInformation,
    employmentInfo: employmentInfo as EmploymentInformation,
    taxInfo: taxInfo as TaxInformation,
    pepStatus: pepStatus as PEPStatus,
    wealthSources: mappedWealthSources,
    incomeSources: mappedIncomeSources,
    // TASK-322: Income summary fields
    totalAnnualIncome: incomeSummary ? unwrap(incomeSummary.totalAnnualIncome) : undefined,
    incomeCurrency: incomeSummary ? unwrap(incomeSummary.incomeCurrency) : undefined,
    primaryIncomeType: incomeSummary ? unwrap(incomeSummary.primaryIncomeType) : undefined,
    // TASK-320 Phase 6: Business activities
    businessActivities: mappedBusinessActivities,
    // TASK-321: Adverse media screening
    adverseMedia: adverseMedia ? {
      hasAdverseMedia: unwrap(adverseMedia.hasAdverseMedia) || false,
      adverseMediaType: unwrap(adverseMedia.adverseMediaType),
      adverseMediaDetails: unwrap(adverseMedia.adverseMediaDetails),
      sanctionsScreened: unwrap(adverseMedia.sanctionsScreened),
      sanctionsHit: unwrap(adverseMedia.sanctionsHit),
      sanctionsDetails: unwrap(adverseMedia.sanctionsDetails),
      boardPositions: unwrap(adverseMedia.boardPositions),
      // TASK-322: Risk assessment fields
      reputationalRisk: unwrap(adverseMedia.reputationalRisk) as AdverseMediaScreening['reputationalRisk'],
      insiderFlag: unwrap(adverseMedia.insiderFlag),
    } : undefined,
    // TASK-262: Add wealth summary data (flat properties per KYCFormData)
    // Handle case where netWorth is an object {amount, currency} vs plain number
    totalNetWorth: wealthSummary ? extractNumericValue(unwrap(wealthSummary.netWorth)) : undefined,
    liquidNetWorth: wealthSummary ? extractNumericValue(unwrap(wealthSummary.liquidNetWorth)) : undefined,
    primaryWealthSource: wealthSummary ? unwrap(wealthSummary.primaryWealthSource) : undefined,
    wealthNarrative: wealthSummary ? unwrap(wealthSummary.wealthNarrative) : undefined,
    netWorthCurrency: wealthSummary ? unwrap(wealthSummary.netWorthCurrency) : undefined,
    // TASK-320 Phase 2: Personal Background
    // Note: Shared data uses simple strings, KYC form uses enum literals
    // Cast to appropriate types for TypeScript compatibility
    personalBackground: personalBackground ? {
      educationLevel: unwrap(personalBackground.educationLevel) as PersonalBackground['educationLevel'],
      institutionsAttended: unwrap(personalBackground.institutionsAttended) as PersonalBackground['institutionsAttended'],
      professionalCertifications: unwrap(personalBackground.professionalCertifications) as PersonalBackground['professionalCertifications'],
      hobbiesInterests: unwrap(personalBackground.hobbiesInterests),
      familyComposition: unwrap(personalBackground.familyComposition),
      dependentsCount: unwrap(personalBackground.dependentsCount),
      spousePartnerEmploymentStatus: unwrap(personalBackground.spouseEmploymentStatus) as PersonalBackground['spousePartnerEmploymentStatus'],
    } : undefined,
    // TASK-320 Phase 3: Asset Breakdown
    assetBreakdownCash: assetBreakdown ? unwrap(assetBreakdown.cash) : undefined,
    assetBreakdownSecurities: assetBreakdown ? unwrap(assetBreakdown.securities) : undefined,
    assetBreakdownRealEstate: assetBreakdown ? unwrap(assetBreakdown.real_estate) : undefined,
    assetBreakdownBusinessEquity: assetBreakdown ? unwrap(assetBreakdown.business_equity) : undefined,
    assetBreakdownCommodities: assetBreakdown ? unwrap(assetBreakdown.commodities) : undefined,
    assetBreakdownCrypto: assetBreakdown ? unwrap(assetBreakdown.crypto) : undefined,
    assetBreakdownOther: assetBreakdown ? unwrap(assetBreakdown.other) : undefined,
    // TASK-320 Phase 3: Additional Wealth Info
    inheritanceExpected: additionalWealthInfo ? unwrap(additionalWealthInfo.inheritanceExpected) : undefined,
    inheritanceDetails: additionalWealthInfo ? unwrap(additionalWealthInfo.inheritanceDetails) : undefined,
    unusualTransactions: additionalWealthInfo ? unwrap(additionalWealthInfo.unusualTransactions) : undefined,
    unusualTransactionDetails: additionalWealthInfo ? unwrap(additionalWealthInfo.unusualTransactionDetails) : undefined,
    sourceDocumentation: additionalWealthInfo ? unwrap(additionalWealthInfo.sourceDocumentation) : undefined,
    // TASK-320 Phase 4: Income Expectations
    expectedIncomeChange: incomeExpectations ? unwrap(incomeExpectations.expectedIncomeChange) : undefined,
    expectedChangeDetails: incomeExpectations ? unwrap(incomeExpectations.expectedChangeDetails) : undefined,
    retirementIncome: incomeExpectations ? unwrap(incomeExpectations.retirementIncome) : undefined,
    retirementIncomeDetails: incomeExpectations ? unwrap(incomeExpectations.retirementIncomeDetails) : undefined,
    otherFinancialSupport: incomeExpectations ? unwrap(incomeExpectations.otherFinancialSupport) : undefined,
    otherSupportDetails: incomeExpectations ? unwrap(incomeExpectations.otherSupportDetails) : undefined,
    documents: [],
    riskAssessment: {
      riskLevel: RiskLevel.LOW,
      riskScore: 0,
      riskFactors: [],
      requiresEnhancedDueDiligence: false,
    },
    metadata: {
      dataSource: 'import',
      dataQualityScore: Math.round(avgConfidence * 100),
    },
  };
}

/**
 * Map KYCFormData back to ClientComplianceData
 *
 * @param formData - KYC form data
 * @param source - Data source for tracking
 * @returns Partial shared compliance data
 */
export function mapFromKYC(
  formData: Partial<KYCFormData>,
  source: DataSource
): Partial<ClientComplianceData> {
  const personal = formData.personalInfo;
  const contact = formData.contactInfo;
  const employment = formData.employmentInfo;
  const tax = formData.taxInfo;
  const pep = formData.pepStatus;

  const createField = <T>(value: T | undefined): TrackedField<T> | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    return createTrackedField(value as T, source);
  };

  return {
    personal: {
      firstName: createField(personal?.firstName),
      middleName: createField(personal?.middleName),
      lastName: createField(personal?.lastName),
      dateOfBirth: createField(personal?.dateOfBirth ? transformDate(personal.dateOfBirth) : undefined),
      placeOfBirth: createField(personal?.placeOfBirth),
      gender: createField(personal?.gender) as TrackedField<'male' | 'female' | 'other' | 'prefer_not_to_say'> | undefined,
      maritalStatus: createField(personal?.maritalStatus) as TrackedField<'single' | 'married' | 'divorced' | 'widowed' | 'separated' | 'domestic_partnership'> | undefined,
      nationalities: createField(personal?.nationalities),
      languages: createField(personal?.languages),
      passportNumber: createField(personal?.passportNumber),
      nationalId: createField(personal?.nationalId),
    },
    contact: {
      phonePrimary: createField(contact?.phonePrimary),
      phoneSecondary: createField(contact?.phoneSecondary),
      emailPrimary: createField(contact?.emailPrimary),
      emailSecondary: createField(contact?.emailSecondary),
      residentialAddress: createField(contact?.residentialAddress) as TrackedField<Address> | undefined,
      mailingAddress: createField(contact?.mailingAddress) as TrackedField<Address> | undefined,
      preferredContactMethod: createField(contact?.preferredContactMethod) as TrackedField<'email' | 'phone' | 'whatsapp' | 'video_call' | 'in_person'> | undefined,
    },
    employment: {
      employmentStatus: createField(employment?.employmentStatus) as TrackedField<'employed' | 'selfEmployed' | 'unemployed' | 'retired' | 'student' | 'other'> | undefined,
      occupation: createField(employment?.occupation),
      employerName: createField(employment?.employerName),
      employmentIndustry: createField(employment?.employmentIndustry),
      yearsEmployed: createField(employment?.yearsEmployed),
      workAddress: createField(employment?.workAddress) as TrackedField<Address> | undefined,
    },
    tax: {
      taxResidence: createField(tax?.taxResidence),
      taxId: createField(tax?.taxId),
      usPerson: createField(tax?.usPerson),
      fatcaStatus: createField(tax?.fatcaStatus) as TrackedField<'compliant' | 'non_compliant' | 'pending' | 'exempt'> | undefined,
      taxJurisdictions: createField(tax?.taxJurisdictions),
    },
    pep: {
      isPep: createField(pep?.isPEP),
      isRca: createField(pep?.isRCA),
      pepType: createField(pep?.pepType) as TrackedField<'domestic' | 'foreign' | 'international'> | undefined,
      pepPosition: createField(pep?.positions?.[0]?.title),
      pepCountry: createField(pep?.countries?.[0]),
      pepStartDate: createField(pep?.startDate ? transformDate(pep.startDate) : undefined),
      pepEndDate: createField(pep?.endDate ? transformDate(pep.endDate) : undefined),
    },
  };
}

/**
 * Create a KYC data source
 */
export function createKYCSource(clientId: string, userId?: string): DataSource {
  return createDataSource(
    'manual',
    `kyc_${clientId}_${Date.now()}`,
    'KYC Form',
    1.0,
    undefined,
    userId
  );
}

/**
 * Collect all confidence scores from compliance data
 */
function collectConfidenceScores(data: ClientComplianceData): number[] {
  const scores: number[] = [];

  const extractScores = (obj: Record<string, unknown>) => {
    Object.values(obj).forEach((value) => {
      if (value && typeof value === 'object' && 'confidence' in value) {
        scores.push((value as TrackedField<unknown>).confidence);
      }
    });
  };

  // Original sections
  if (data.personal) extractScores(data.personal as Record<string, unknown>);
  if (data.contact) extractScores(data.contact as Record<string, unknown>);
  if (data.employment) extractScores(data.employment as Record<string, unknown>);
  if (data.financial) extractScores(data.financial as Record<string, unknown>);
  if (data.tax) extractScores(data.tax as Record<string, unknown>);
  if (data.pep) extractScores(data.pep as Record<string, unknown>);
  // TASK-262: New sections
  if (data.identification) extractScores(data.identification as Record<string, unknown>);
  if (data.personalBackground) extractScores(data.personalBackground as Record<string, unknown>);
  if (data.wealthSummary) extractScores(data.wealthSummary as Record<string, unknown>);
  if (data.additionalWealthInfo) extractScores(data.additionalWealthInfo as Record<string, unknown>);
  if (data.incomeExpectations) extractScores(data.incomeExpectations as Record<string, unknown>);
  // Extract from arrays
  if (data.wealthSources) {
    data.wealthSources.forEach((source) => extractScores(source as Record<string, unknown>));
  }
  if (data.incomeSources) {
    data.incomeSources.forEach((source) => extractScores(source as Record<string, unknown>));
  }
  if (data.businessActivities) {
    data.businessActivities.forEach((activity) => extractScores(activity as Record<string, unknown>));
  }

  return scores;
}

/**
 * Calculate average confidence from compliance data
 */
export function calculateAverageConfidence(data: ClientComplianceData): number {
  const scores = collectConfidenceScores(data);
  if (scores.length === 0) return 0;
  return scores.reduce((a, b) => a + b, 0) / scores.length;
}
